/**
 * Document Templates Zustand Store
 *
 * Manages state for Global Document Templates including:
 * - Template list with filters and pagination
 * - Active template details
 * - Template CRUD operations
 * - Visibility management
 */
import { create } from 'zustand';
import { getApiClient } from '@respectly/api-client';
import type {
  GlobalTemplate,
  PaginationMeta,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface DocumentTemplateFilters {
  search?: string;
  category?: string | null; // sales, hr, legal, real_estate, healthcare, finance, general
  visibility?: 'all' | 'plans' | 'tenants' | 'none';
  isActive?: boolean;
}

interface CreateDocumentTemplateDto {
  name: string;
  description?: string;
  content: string; // JSON string
  category?: string;
  visibility?: 'all' | 'plans' | 'tenants' | 'none';
  visibleToPlans?: string[];
  visibleToTenants?: string[];
  isActive?: boolean;
}

interface UpdateDocumentTemplateDto {
  name?: string;
  description?: string;
  content?: string; // JSON string
  category?: string;
  visibility?: 'all' | 'plans' | 'tenants' | 'none';
  visibleToPlans?: string[];
  visibleToTenants?: string[];
  isActive?: boolean;
}

interface DocumentTemplatesError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface DocumentTemplatesState {
  // Templates List
  templates: GlobalTemplate[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: DocumentTemplatesError | null;

  // Filters
  filters: DocumentTemplateFilters;

  // Active Template
  activeTemplateId: string | null;
  activeTemplate: GlobalTemplate | null;
  isLoadingTemplate: boolean;
  templateError: DocumentTemplatesError | null;

  // Action States
  isPerformingAction: boolean;
  actionError: DocumentTemplatesError | null;

  // Actions - List
  loadTemplates: (page?: number) => Promise<void>;
  refreshTemplates: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof DocumentTemplateFilters>(key: K, value: DocumentTemplateFilters[K]) => void;
  clearFilters: () => void;

  // Actions - Active Template
  loadTemplate: (id: string) => Promise<void>;
  selectTemplate: (id: string) => void;
  clearActiveTemplate: () => void;

  // Actions - CRUD
  createTemplate: (dto: CreateDocumentTemplateDto) => Promise<string | null>;
  updateTemplate: (id: string, dto: UpdateDocumentTemplateDto) => Promise<boolean>;
  duplicateTemplate: (id: string, newName?: string) => Promise<string | null>;
  deleteTemplate: (id: string) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Templates List
  templates: [] as GlobalTemplate[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as DocumentTemplatesError | null,

  // Filters
  filters: {} as DocumentTemplateFilters,

  // Active Template
  activeTemplateId: null as string | null,
  activeTemplate: null as GlobalTemplate | null,
  isLoadingTemplate: false,
  templateError: null as DocumentTemplatesError | null,

  // Action States
  isPerformingAction: false,
  actionError: null as DocumentTemplatesError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useDocumentTemplatesStore = create<DocumentTemplatesState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadTemplates: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();

      // Always filter by type=document
      params.set('type', 'document');
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.visibility) params.set('visibility', filters.visibility);
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));

      const response = await api.get<{
        success: boolean;
        data: { templates: GlobalTemplate[]; pagination: PaginationMeta };
      }>(`/api/templates?${params.toString()}`);

      set({
        templates: response.data.templates,
        pagination: response.data.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_TEMPLATES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load document templates',
        },
      });
    }
  },

  refreshTemplates: async () => {
    const { pagination } = get();
    await get().loadTemplates(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadTemplates(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadTemplates(1);
  },

  // ============================================
  // Active Template Actions
  // ============================================

  loadTemplate: async (id: string) => {
    set({ activeTemplateId: id, isLoadingTemplate: true, templateError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: GlobalTemplate;
      }>(`/api/templates/${id}`);

      set({ activeTemplate: response.data, isLoadingTemplate: false });
    } catch (error) {
      set({
        isLoadingTemplate: false,
        templateError: {
          code: 'LOAD_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load template',
        },
      });
    }
  },

  selectTemplate: (id: string) => {
    const { templates } = get();
    const template = templates.find((t) => t.id === id);
    if (template) {
      set({ activeTemplateId: id, activeTemplate: template });
    } else {
      get().loadTemplate(id);
    }
  },

  clearActiveTemplate: () => {
    set({
      activeTemplateId: null,
      activeTemplate: null,
      templateError: null,
    });
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createTemplate: async (dto: CreateDocumentTemplateDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: GlobalTemplate;
      }>('/api/templates', {
        type: 'document',
        ...dto,
      });

      // Refresh the list
      await get().refreshTemplates();
      set({ isPerformingAction: false });
      return response.data.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create document template',
        },
      });
      return null;
    }
  },

  updateTemplate: async (id: string, dto: UpdateDocumentTemplateDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: GlobalTemplate;
      }>(`/api/templates/${id}`, dto);

      // Update local state
      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, ...response.data } : t
        ),
        activeTemplate:
          state.activeTemplate?.id === id
            ? { ...state.activeTemplate, ...response.data }
            : state.activeTemplate,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update document template',
        },
      });
      return false;
    }
  },

  duplicateTemplate: async (id: string, newName?: string): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: GlobalTemplate;
      }>(`/api/templates/${id}/clone`, newName ? { name: newName } : {});

      // Refresh the list
      await get().refreshTemplates();
      set({ isPerformingAction: false });
      return response.data.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DUPLICATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to duplicate document template',
        },
      });
      return null;
    }
  },

  deleteTemplate: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(`/api/templates/${id}`);

      // Remove from local state
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        activeTemplate: state.activeTemplate?.id === id ? null : state.activeTemplate,
        activeTemplateId: state.activeTemplateId === id ? null : state.activeTemplateId,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete document template',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, templateError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get templates by category */
export const useDocumentTemplatesByCategory = (category: string) => {
  return useDocumentTemplatesStore((state) =>
    state.templates.filter((t) => t.category === category)
  );
};

/** Get public templates count */
export const usePublicDocumentTemplatesCount = () => {
  return useDocumentTemplatesStore((state) =>
    state.templates.filter((t) => t.visibility === 'all').length
  );
};

/** Get active templates count */
export const useActiveDocumentTemplatesCount = () => {
  return useDocumentTemplatesStore((state) =>
    state.templates.filter((t) => t.isActive).length
  );
};

/** Check if any loading state is active */
export const useDocumentTemplatesLoading = () => {
  const isLoadingList = useDocumentTemplatesStore((state) => state.isLoadingList);
  const isLoadingTemplate = useDocumentTemplatesStore((state) => state.isLoadingTemplate);
  const isPerformingAction = useDocumentTemplatesStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingTemplate || isPerformingAction;
};

/** Get template by ID */
export const useDocumentTemplateById = (id: string) => {
  return useDocumentTemplatesStore((state) =>
    state.templates.find((t) => t.id === id)
  );
};

/** Get active filters count */
export const useActiveDocumentFiltersCount = () => {
  return useDocumentTemplatesStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.category) count++;
    if (state.filters.visibility) count++;
    if (state.filters.isActive !== undefined) count++;
    return count;
  });
};
